import PropTypes from "prop-types";
import { useMemo, useState } from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Chip from "@mui/material/Chip";
import { useTheme, alpha } from "@mui/material/styles";
import MDTypography from "components/MDTypography";

function pad2(n) {
  return String(n).padStart(2, "0");
}

/** Local date string YYYY-MM-DD (no UTC shift for calendar cells) */
export function toYmd(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function parseYmd(ymd) {
  const [y, m, day] = ymd.split("-").map(Number);
  return new Date(y, m - 1, day);
}

function startOfToday() {
  const t = new Date();
  return new Date(t.getFullYear(), t.getMonth(), t.getDate());
}

function isBeforeDay(a, b) {
  return a.getTime() < b.getTime();
}

function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function addMonths(d, n) {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}

/** Days in month grid, leading/trailing blanks as null */
function buildMonthGrid(viewMonth) {
  const year = viewMonth.getFullYear();
  const month = viewMonth.getMonth();
  const first = new Date(year, month, 1);
  const startWeekday = first.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startWeekday; i += 1) cells.push(null);
  for (let d = 1; d <= daysInMonth; d += 1) cells.push(new Date(year, month, d));
  while (cells.length % 7 !== 0) cells.push(null);
  for (let i = cells.length; i < 42; i += 1) cells.push(null);
  return cells.slice(0, 42);
}

export default function DemoCalendarPicker({ selectedDates, onChange, maxSelections=2 }) {
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  const today = startOfToday();
  const [viewMonth, setViewMonth] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));

  const selectedSet = useMemo(() => new Set(selectedDates || []), [selectedDates]);

  const nextWeekRange = useMemo(() => {
    const d = new Date(today);
    const dow = d.getDay();
    const mondayOffset = dow === 0 ? -6 : 1 - dow;
    const nextMonday = new Date(d);
    nextMonday.setDate(d.getDate() + mondayOffset + 7);
    const end = new Date(nextMonday);
    end.setDate(nextMonday.getDate() + 6);
    return { start: nextMonday, end };
  }, [today]);

  const grid = useMemo(() => buildMonthGrid(viewMonth), [viewMonth]);

  const monthLabel = viewMonth.toLocaleString(undefined, { month: "long", year: "numeric" });
  const weekdayLabels = ["Su", "Mo", "Tu", "Wed", "Th", "Fr", "Sa"];

  const toggleDate = (ymd) => {
    const next = new Set(selectedSet);
    if (next.has(ymd)) {
      next.delete(ymd);
      onChange([...next].sort());
      return;
    }
    if (next.size >= maxSelections) {
      const arr = [...next];
      arr.shift();
      arr.push(ymd);
      onChange(arr.sort());
      return;
    }
    next.add(ymd);
    onChange([...next].sort());
  };

  return (
    <Box sx={{ width: "100%" }}>
      <MDTypography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
        Select {maxSelections} preferred dates for your demo
      </MDTypography>

      {selectedDates?.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
          {selectedDates.map((ymd) => {
            const d = parseYmd(ymd);
            const label = d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
            return (
              <Chip
                key={ymd}
                label={label}
                onDelete={() => toggleDate(ymd)}
                color="primary"
                variant="filled"
                sx={{ fontWeight: 600 }}
              />
            );
          })}
        </Box>
      )}

      <Box
        sx={{
          width: "100%",
          borderRadius: 3,
          border: `1px solid ${theme.palette.divider}`,
          bgcolor: "background.paper",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 1,
            py: 0.5,
            borderBottom: `1px solid ${theme.palette.divider}`,
            bgcolor: alpha(primary, theme.palette.mode === "dark" ? 0.08 : 0.04),
          }}
        >
          <IconButton size="small" onClick={() => setViewMonth((m) => addMonths(m, -1))} aria-label="Previous month">
            <ChevronLeftIcon />
          </IconButton>
          <MDTypography variant="button" fontWeight={700} color="text.primary">
            {monthLabel}
          </MDTypography>
          <IconButton size="small" onClick={() => setViewMonth((m) => addMonths(m, 1))} aria-label="Next month">
            <ChevronRightIcon />
          </IconButton>
        </Box>

        <Box sx={{ px: 1, py: 1 }}>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 0.5, mb: 0.5 }}>
            {weekdayLabels.map((w) => (
              <MDTypography key={w} variant="caption" color="text.secondary" sx={{ textAlign: "center", fontWeight: 600 }}>
                {w}
              </MDTypography>
            ))}
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 0.5 }}>
            {grid.map((cell, idx) => {
              if (!cell) {
                return <Box key={`e-${idx}`} sx={{ aspectRatio: "1", minHeight: 36 }} />;
              }
              const ymd = toYmd(cell);
              const past = isBeforeDay(cell, today);
              const selected = selectedSet.has(ymd);
              const inNextWeek =
                !past &&
                !isBeforeDay(cell, nextWeekRange.start) &&
                !isBeforeDay(nextWeekRange.end, cell);
              const isToday = isSameDay(cell, today);

              return (
                <Box
                  key={ymd}
                  component="button"
                  type="button"
                  disabled={past}
                  onClick={() => !past && toggleDate(ymd)}
                  sx={{
                    aspectRatio: "1",
                    minHeight: 36,
                    maxHeight: 44,
                    border: "1px solid",
                    borderColor: selected ? primary : theme.palette.divider,
                    borderRadius: 2,
                    bgcolor: selected
                      ? alpha(primary, theme.palette.mode === "dark" ? 0.35 : 0.2)
                      : inNextWeek
                        ? alpha(primary, 0.06)
                        : "transparent",
                    color: past ? "text.disabled" : "text.primary",
                    cursor: past ? "not-allowed" : "pointer",
                    fontWeight: selected ? 700 : isToday ? 600 : 500,
                    fontSize: "0.8125rem",
                    transition: "opacity 0.15s ease, box-shadow 0.15s ease, transform 0.12s ease",
                    boxShadow: selected ? `0 2px 8px ${alpha(primary, 0.35)}` : "none",
                    "&:hover": past
                      ? {}
                      : {
                          borderColor: primary,
                          bgcolor: alpha(primary, theme.palette.mode === "dark" ? 0.12 : 0.1),
                          transform: "scale(1.03)",
                        },
                  }}
                >
                  {cell.getDate()}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

DemoCalendarPicker.propTypes = {
  selectedDates: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func.isRequired,
  maxSelections: PropTypes.number,
};

DemoCalendarPicker.defaultProps = {
  selectedDates: [],
  maxSelections: 2,
};
